import { CitationData } from 'src/citation-generator/types';
import { BookmarkServerResponse, TemplateDocument } from './api';
import { AsyncStatusEnum } from './common';

/**
 * Project can be in one of the following states:
 * - Not started ==> IDLE
 * - project is success restored ==> RESTORED
 * - project is being restored ==> RESTORING
 * - project is being created ==> CREATING
 * - project is being saved ==> SAVING
 * - project is saved ==> SAVED
 * - project is not-saved or some error occurred while saving ==> UNSAVED
 *
 */
export enum ProjectStatusEnum {
  IDLE = 'IDLE',

  CREATING = 'CREATING',
  CREATED = 'CREATED',

  SAVING = 'SAVING',
  SAVED = 'SAVED',
  UNSAVED = 'UNSAVED',

  RESTORING = 'RESTORING',
  RESTORED = 'RESTORED',
}

/**
 * Project React State
 */
export interface ProjectState {
  title: string;
  id: string;
  status: ProjectStatusEnum;
  numberOfWords: number;
  numberOfErrors: number;
  lastSaved: number;
  shouldUpdateTime?: boolean;
}

export enum ProjectStateActionName {
  SetProjectTitle = 'rshr/user/changed_project_name',
  SetProjectStatus = 'rshr/system/project/set_project_state',
  SetProjectId = 'rshr/system/project/set_project_id',
  SetProjectWordCount = 'rshr/system/project/set_project_word_count',
  SetProjectErrorCount = 'rshr/system/project/set_project_error_count',
  SetLastSavedTime = 'rshr/system/project/set_last_saved',
  RestoreToInitStateOfProjectState = 'rshr/system/project/restore_to_init_project_state',
  SetProjectState = 'rshr/system/project/set_project_state',
  SetStorageUsed = 'rshr/system/project/set_storage_used',
  /** Async actions */
  RestoringProject = 'rshr/user/project/restoring_project',
  RestoredProject = 'rshr/system/project/restored_project',
  CreatingProject = 'rshr/user/project/creating_project',
  CreatedProject = 'rshr/user/created_new_project',
  SavedProjectSuccessfully = 'rshr/system/project/saved_project_successfully',
}

export interface SetProjectTitleAction {
  type: ProjectStateActionName.SetProjectTitle;
  payload: { title: string };
}

export interface SetProjectStatusAction {
  type: ProjectStateActionName.SetProjectStatus;
  payload: { status: ProjectStatusEnum; shouldUpdateTime: boolean };
}

export interface SetProjectIdAction {
  type: ProjectStateActionName.SetProjectId;
  payload: { id: string };
}

export interface SetProjectWordCountAction {
  type: ProjectStateActionName.SetProjectWordCount;
  payload: { numberOfWords: number };
}

export interface SetProjectErrorCountAction {
  type: ProjectStateActionName.SetProjectErrorCount;
  payload: { numberOfErrors: number };
}

export interface SetProjectLastSavedTimeAction {
  type: ProjectStateActionName.SetLastSavedTime;
  payload?: { time?: number };
}

export interface RestoreToInitStateOfProjectStateAction {
  type: ProjectStateActionName.RestoreToInitStateOfProjectState;
}

export interface SetProjectStateAction {
  type: ProjectStateActionName.SetProjectState;
  payload: ProjectState;
}

export interface RestoredProjectFromDbAction {
  type: ProjectStateActionName.RestoredProject;
  payload: RestoreProjectAPIResponse;
}

export interface CreatedProjectFromDbAction {
  type: ProjectStateActionName.CreatedProject;
  payload: CreateProjectAPIResponse;
}

export interface SavedProjectSuccessfullyAction {
  type: ProjectStateActionName.SavedProjectSuccessfully;
  payload: SavedProjectAPIResponse;
}

export type ProjectActions =
  | SetProjectIdAction
  | SetProjectTitleAction
  | SetProjectStatusAction
  | SetProjectWordCountAction
  | SetProjectErrorCountAction
  | SetProjectLastSavedTimeAction
  | RestoreToInitStateOfProjectStateAction
  | SetProjectStateAction
  | RestoredProjectFromDbAction
  | CreatedProjectFromDbAction
  | SavedProjectSuccessfullyAction;

export interface RestoreProjectAPIResponse {
  id: string;
  exists: boolean;
  title: string;
  bookmarks: BookmarkServerResponse[];
  content: string;
  scratchpad: string;
  numberOfWords: number;
  citations: CitationData[];
  totalStorageUsed: number;
  citationStyleId?: string;
}
export interface ListProjectsAPIResponseProjectDetail {
  createdAt: string;
  updatedAt: string;
  preview: string;
  storageUsed: number;
  title: string;
  id: string;
}
export interface ListProjectAPIResponse {
  projects: ListProjectsAPIResponseProjectDetail[];
  totalStorageUsed: number;
  total: number;
  templates: TemplateDocument[];
}
export interface CreateProjectAPIResponse {
  id: string;
  totalStorageUsed: number;
  title: string;
}

export interface CreateProjectAPIRequest {
  title: string;
  html: string;
  template?: string;
}

export interface SavedProjectAPIResponse {
  id: string;
  totalStorageUsed: number;
}
